import Button from '@ssg/common/Components/Button';
import Header from '@ssg/common/Components/Header';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Box from '../../../Components/Layout/Box';
import TextAreaInput from './Inputs/TextAreaInput';
import ReportStaticInfo from './ReportStaticInfo';
import { faCheck, faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import Loading from '@ssg/common/Components/Loading';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import Datepicker from '@ssg/common/Components/Datepicker';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { NoticeOfConstructionwasteFormData, useCreateNoticeOfConstructionwasteReportMutation, useGetCaseAsbestosReportsAndDocumentsQuery } from '@ssg/common/GraphQL/indexV2';
import TextInput from './Inputs/TextInput';

interface Params {
	id: string;
}
interface ReportError {
	key: keyof NoticeOfConstructionwasteFormData;
}
const throwErrorMessage = (key: keyof NoticeOfConstructionwasteFormData, reportErrors: ReportError[]): string | undefined => {
	const isError = reportErrors.filter(re => re.key === key).length > 0;
	if (isError) return 'error.required';
	return;
};

const NoticeOfConstructionWasteReport: React.FC = (): React.ReactElement => {
	const { id: caseId } = useParams<Params>();
	const { t } = useTranslation();
	const history = useHistory();

	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);

	const { loading: caseReportsAndDocumentsLoading } = useGetCaseAsbestosReportsAndDocumentsQuery({
		variables: {
			id: caseId,
		},
		skip: !caseId || caseId === '',
		fetchPolicy: 'cache-and-network',
		onCompleted(data) {
			const formData = removeTypenameGeneric(data.case.caseAsbestosReports?.noticeOfConstructionWasteFormData);

			if (formData) {
				if (formData.area) setArea(formData.area);
				if (formData.assignment) setAssignment(formData.assignment);
				if (formData.carrier) setCarrier(formData.carrier);
				if (formData.wasteFractions) setWasteFractions(formData.wasteFractions);
				if (formData.wastePackaging) setWastePackaging(formData.wastePackaging);

				if (formData.yearOfConstruction) setyearOfConstruction(formData.yearOfConstruction);
				if (formData.startDate) setStartDate(formatDateForInput(new Date(formData.startDate)));
				if (formData.endDate) setEndDate(formatDateForInput(new Date(formData.endDate)));
			}
		},
	});

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${caseId}-notice-of-constructionwaste${name}`, initialValue);

	// Form values

	const [yearOfConstruction, setyearOfConstruction] = useLocalStorage('yearOfConstruction-noticeOfConstructionWaste', '');
	const [area, setArea] = useLocalStorage('area-noticeOfConstructionWaste', '');
	const [assignment, setAssignment] = useLocalStorage('assignment-noticeOfConstructionWaste', '');
	const [wasteFractions, setWasteFractions] = useLocalStorage('wasteFractions-noticeOfConstructionWaste', '');
	const [wastePackaging, setWastePackaging] = useLocalStorage('wastePackaging-noticeOfConstructionWaste', '');
	const [carrier, setCarrier] = useLocalStorage('carrier-noticeOfConstructionWaste', '');
	const [startDate, setStartDate] = useLocalStorage('startDate-noticeOfConstructionWaste', formatDateForInput(new Date()));
	const [endDate, setEndDate] = useLocalStorage('endDate-noticeOfConstructionWaste', formatDateForInput(new Date()));

	const [submitted, setSubmitted] = React.useState(false);
	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');
	const [reportErrors, setReportErrors] = React.useState<ReportError[]>([]);
	const [createNoticeOfConstructionWasteReport, { loading: submitting }] = useCreateNoticeOfConstructionwasteReportMutation();

	const errorCheck = (formData: Pick<NoticeOfConstructionwasteFormData, 'startDate' | 'endDate' | 'assignment' | 'wastePackaging' | 'carrier'>) => {
		const errors: ReportError[] = [];

		if (!formData.startDate || formData.startDate === 'NaN-aN-aN' || formData.startDate === undefined) {
			errors.push({ key: 'startDate' });
		}
		if (!formData.endDate || formData.endDate === 'NaN-aN-aN' || formData.endDate === undefined) {
			errors.push({ key: 'endDate' });
		}
		if (!formData.assignment || formData.assignment.length === 0) {
			errors.push({ key: 'assignment' });
		}
		if (!formData.wastePackaging || formData.wastePackaging.length === 0) {
			errors.push({ key: 'wastePackaging' });
		}
		if (!formData.carrier || formData.carrier.length === 0) {
			errors.push({ key: 'carrier' });
		}

		return errors;
	};

	async function submitReport() {
		setSubmitted(true);

		const formData = {
			area,
			assignment,
			wasteFractions,
			wastePackaging,
			carrier,
			yearOfConstruction: yearOfConstruction,
			startDate: formatDateForInput(new Date(startDate)),
			endDate: formatDateForInput(new Date(endDate)),
		};

		const errors = errorCheck(formData);
		setReportErrors(errors);
		if (errors.length > 0) {
			return;
		}
		try {
			setLoadingType('CREATE');
			await createNoticeOfConstructionWasteReport({
				variables: {
					caseId,
					formData: formData,
					onlySave: false,
				},
			});

			history.goBack();
		} catch (e) {
			console.error(e);
		}
	}
	React.useEffect(() => {
		setSaved(false);
	}, [area, assignment, wasteFractions, wastePackaging, carrier, yearOfConstruction, startDate, endDate]);

	async function saveReport() {
		const formData: NoticeOfConstructionwasteFormData = {
			area,
			assignment,
			wasteFractions,
			wastePackaging,
			carrier,
			yearOfConstruction,
			startDate: formatDateForInput(new Date(startDate)),
			endDate: formatDateForInput(new Date(endDate)),
		};
		try {
			setLoadingType('SAVE');
			await createNoticeOfConstructionWasteReport({
				variables: {
					caseId,
					formData: formData,
					onlySave: true,
				},
			});
			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}
	if (caseReportsAndDocumentsLoading) return <Loading />;

	return (
		<div>
			<Header title="reports.reportTypes.noticeOfConstructionwaste" backButton />

			<div className="space-y-8 px-10 pb-10 text-sm">
				<ReportStaticInfo caseId={caseId} />

				<Box full>
					<TextInput label={t('reports.buildYear')} placeholder={t('reports.buildYear')} value={yearOfConstruction} onChange={setyearOfConstruction} />

					<TextInput label={t('reports.squareMeters')} placeholder={t('reports.squareMeters')} value={area} onChange={setArea} />

					<Datepicker
						title="reports.startDate"
						value={startDate}
						onChange={e => setStartDate(e.target.value)}
						defaultValue={undefined}
						name="reports.startDate"
						errorMessage={throwErrorMessage('startDate', reportErrors)}
					/>

					<Datepicker
						title="reports.endDate"
						value={endDate}
						onChange={e => setEndDate(e.target.value)}
						defaultValue={undefined}
						name="reports.endDate"
						errorMessage={throwErrorMessage('endDate', reportErrors)}
					/>

					<TextAreaInput
						label={t('reports.asbestos.assignment')}
						placeholder={t('reports.asbestos.assignmentPlaceholder')}
						value={assignment}
						onChange={setAssignment}
						errorMessage={throwErrorMessage('assignment', reportErrors)}
					/>

					<TextAreaInput label={t('reports.asbestos.wasteFractions')} placeholder={t('reports.asbestos.wasteFractionsPlaceholder')} value={wasteFractions} onChange={setWasteFractions} />

					<TextAreaInput
						label={t('reports.asbestos.wastePackaging')}
						placeholder={t('reports.asbestos.wastePackagingPlaceholder')}
						value={wastePackaging}
						onChange={setWastePackaging}
						errorMessage={throwErrorMessage('wastePackaging', reportErrors)}
					/>

					<TextInput
						label={t('reports.asbestos.carrier')}
						placeholder={t('reports.asbestos.carrierPlaceholder')}
						value={carrier}
						onChange={setCarrier}
						errorMessage={throwErrorMessage('carrier', reportErrors)}
					/>
				</Box>

				<div className="ml-4 my-2 mr-6 flex flex-row space-x-2">
					<Button icon={faFilePdf} onClick={() => submitReport()} disabled={submitting} loading={submitting && loadingType === 'CREATE'} success text="reports.createPdfReport" />

					{saveButtonReportsFlag && (
						<Button
							disabled={submitting}
							loading={submitting && loadingType === 'SAVE'}
							primary
							onClick={() => saveReport()}
							text={saved ? '' : 'reports.saveReport'}
							icon={saved ? faCheck : undefined}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default NoticeOfConstructionWasteReport;
